import { action, computed, observable } from 'mobx'
import { FeatureToggles } from '@/features/feature-toggles'
import type { Observable } from 'rxjs'
import type {
  GetCurrentMemberFn,
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetCurrentWorkspaceFn,
  GetEnterpriseConnectionFn,
  GetMultiFactorAuthEnrollmentUrlFn,
  HasMultipleWorkspacesFn,
  IsEnrolledInMultiFactorAuthFn,
  IsWorkspaceSelectorOpenFn,
  OpenWorkspaceSearchFn,
  GetTeamMembersForWorkspaceMemberFn,
  GetUserDataFn,
  ToggleWorkspaceSelectorFn,
} from '@/features/iam'
import type { PromptCreateJobFn } from '@/features/jobs'
import type { CanViewPayoutsAdminFn } from '@/features/payouts'
import type { CanViewLegendsFn } from '@/features/legends'
import type { ToggleNotificationsSidebarFn } from '@/features/notifications'
import type { AlertErrorHandlerFactory } from '@/features/shell/alerts/Alert'
import { gotoHQ } from './hq'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction } from '@/utils/mobx-util'
import { localStorage } from '@/infra/storage'

/**
 * A workspace's own link in the navigation.
 */
export interface CustomNavigationLink {
  label: string
  url: string
}

/**
 * View model for the global navigation, in both the classic top navigation
 * and the refreshed sidebar.
 */
export class GlobalNavigationViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly profileMenu = new ModalViewModel()

  @observable accessor canViewOpportunities = false
  @observable accessor canViewProPayouts = false
  @observable accessor notificationCount = 0
  @observable accessor opportunityCount = 0
  @observable accessor unreadMessagesCount = 0

  private readonly _collapsedSections = observable.map<string, boolean>()

  constructor(
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn,
    private readonly _getUserData: GetUserDataFn,
    private readonly _hasMultipleWorkspaces: HasMultipleWorkspacesFn,
    private readonly _isWorkspaceSelectorOpen: IsWorkspaceSelectorOpenFn,
    private readonly _toggleWorkspaceSelector: ToggleWorkspaceSelectorFn,
    private readonly _openWorkspaceSearch: OpenWorkspaceSearchFn,
    private readonly _isEnrolledInMultiFactorAuth: IsEnrolledInMultiFactorAuthFn,
    private readonly _getMultiFactorAuthEnrollmentUrl: GetMultiFactorAuthEnrollmentUrlFn,
    private readonly _getEnterpriseConnection: GetEnterpriseConnectionFn,
    private readonly _promptCreateJob: PromptCreateJobFn,
    private readonly _canViewPayoutsAdmin: CanViewPayoutsAdminFn,
    private readonly _canViewLegends: CanViewLegendsFn,
    private readonly _toggleNotificationsSidebar: ToggleNotificationsSidebarFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    unreadMessagesCount$: Observable<number>,
    canViewOpportunities$: Observable<boolean>,
    opportunityCount$: Observable<number>,
    canViewProPayouts$: Observable<boolean>,
    unreadNotificationsCount$: Observable<number>,
  ) {
    this._lifecycle.manage(
      canViewOpportunities$.pipe(
        tapAction((value) => {
          this.canViewOpportunities = value
        }),
      ),
    )
    this._lifecycle.manage(
      canViewProPayouts$.pipe(
        tapAction((value) => {
          this.canViewProPayouts = value
        }),
      ),
    )
    this._lifecycle.manage(
      unreadNotificationsCount$.pipe(
        tapAction((value) => {
          this.notificationCount = value
        }),
      ),
    )
    this._lifecycle.manage(
      opportunityCount$.pipe(
        tapAction((value) => {
          this.opportunityCount = value
        }),
      ),
    )
    this._lifecycle.manage(
      unreadMessagesCount$.pipe(
        tapAction((value) => {
          this.unreadMessagesCount = value
        }),
      ),
    )
  }

  /**
   * Whether the navigation section is collapsed, remembered across sessions.
   */
  isSectionCollapsed = (key: string) =>
    this._collapsedSections.get(key) ??
    localStorage.getItem(sectionCollapsedStorageKey(key)) === 'true'

  /**
   * Collapses or expands the navigation section.
   */
  @action.bound
  toggleSectionCollapsed(key: string) {
    const collapsed = !this.isSectionCollapsed(key)
    this._collapsedSections.set(key, collapsed)
    localStorage.setItem(sectionCollapsedStorageKey(key), String(collapsed))
  }

  @computed
  get member() {
    return this._getCurrentMember()
  }

  @computed
  get isFirmAdmin() {
    const member = this.member
    return (
      this._getUserData(member.user.publicId)?.userDataType === 'User' &&
      member.workspace.classification === 'B2B' &&
      !member.user.isStaff
    )
  }

  @computed
  get isPro() {
    return this._getUserData(this.member.user.publicId)?.userDataType === 'Pro'
  }

  /**
   * Call agents only get the call center.
   */
  @computed
  get isCallAgent() {
    const publicId = this._getCurrentUser().publicId
    const userType = publicId
      ? this._getUserData(publicId)?.userDataType
      : undefined
    return userType === 'CallAgent'
  }

  @computed
  get canSeeAdminView() {
    return (
      this.canManageJobScopeChangeRequest ||
      this.canManageServices ||
      this.canManageBookkeeping ||
      this.canSeeStaffingManager ||
      this.canViewPayoutsAdmin ||
      this.canManageAccountMergeRequests
    )
  }

  @computed
  get canManageJobScopeChangeRequest() {
    return (
      this.member.user.platformAdmin ||
      this._getCurrentUserWorkspaceMemberships().some(
        (wm) => wm.member.permissions.adminJobScopeChangeManage,
      )
    )
  }

  @computed
  get canManageServices() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (wm) =>
        wm.member.permissions.adminManageGlobalInventoryItems ||
        wm.member.permissions.manageWorkspaceInventoryItems,
    )
  }

  @computed
  get canManageBookkeeping() {
    return (
      FeatureToggles.bookkeeping() &&
      this._getCurrentUserWorkspaceMemberships().some(
        (wm) => wm.member.permissions.manageBookkeeping,
      )
    )
  }

  @computed
  get canSeeStaffingManager() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (wm) =>
        wm.member.permissions.adminStaffingManage &&
        wm.workspace.config.staffing.enabled,
    )
  }

  @computed
  get canManageAccountMergeRequests() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (wm) => wm.member.permissions.adminGlobalManageAccountMergeRequests,
    )
  }

  @computed
  get canViewFirmDashboard() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (wms) => wms.member.permissions.viewFirmDashboard,
    )
  }

  @computed
  get canViewJobs() {
    return Boolean(this._getCurrentWorkspace())
  }

  @computed
  get canViewScopingJobs() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (wsm) =>
        wsm.member.permissions.scopeWork &&
        wsm.member.permissions.adminScopeWorkView,
    )
  }

  /**
   * Whether the user can create jobs in some workspace, either at the
   * workspace or at the team level.
   */
  @computed
  get canDraftJob() {
    return this._getCurrentUserWorkspaceMemberships().some(
      ({ member, workspace }) =>
        member.permissions.startNewJob ||
        member.permissions.adminJobCreate ||
        this._getTeamMembersForWorkspaceMember(
          workspace.id,
          this._getCurrentMember().user.id,
        ).some(
          (tm) => tm.permissions.createTeamJobs || tm.permissions.startTeamJobs,
        ),
    )
  }

  @computed
  get canAccessUserManagement() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (wms) => wms.member.permissions.adminUserList,
    )
  }

  /**
   * Anyone who can manage all users already reaches clients there, so they
   * only get the one user management link.
   */
  @computed
  get canAccessClientUserManagement() {
    return this.member.canListClients && !this.canAccessUserManagement
  }

  @computed
  get canViewTeams() {
    return (
      this.canViewJobs &&
      (this.member.permissions.createTeam ||
        this.member.permissions.adminTeamsView)
    )
  }

  @computed
  get canViewDocuments() {
    return this.member.permissions.docsView
  }

  @computed
  get canViewPayoutsAdmin() {
    return this._canViewPayoutsAdmin()
  }

  @computed
  get canViewLegends() {
    return this._canViewLegends()
  }

  @computed
  get canGoToHq() {
    return this.member.permissions.adminAccess
  }

  @computed
  get canSwitchWorkspaces() {
    return this._hasMultipleWorkspaces() && !this.isFirmAdmin
  }

  @computed
  get isWorkspaceSelectorOpen() {
    return this._isWorkspaceSelectorOpen()
  }

  @computed
  get customLinks(): CustomNavigationLink[] {
    const member = this.member
    return member.workspace.config.customMenu
      .filter(
        (item) =>
          (item.target === 'All' || item.target === 'Portal') &&
          member.roles.some((r) => r.id === item.audience),
      )
      .map((item) => ({ label: item.label, url: item.url }))
  }

  @computed
  get shouldShowBilling() {
    return this.member.workspace.config.billing.enabled
  }

  @computed
  get canEnrollInMultiFactorAuth() {
    return (
      FeatureToggles.multiFactorAuthentication() &&
      // There is a caveat with this approach: we won't observe that the
      // user has enrolled in MFA until the next time they log in.
      !this._isEnrolledInMultiFactorAuth()
    )
  }

  @action.bound
  createJob() {
    return this._promptCreateJob()
  }

  @action.bound
  toggleWorkspaceSelector() {
    this._toggleWorkspaceSelector()
  }

  @action.bound
  toggleNotifications() {
    this._toggleNotificationsSidebar()
  }

  @action.bound
  addNewWorkspace() {
    this._openWorkspaceSearch()
  }

  @action.bound
  async enrollInMultiFactorAuth() {
    const url = await this._getMultiFactorAuthEnrollmentUrl()
      .catch(this._createAlertErrorHandler('Unable to start MFA enrollment'))
      .catch(() => null)
    if (url) {
      window.open(url)
    }
  }

  @action.bound
  goToHq() {
    gotoHQ(this._getEnterpriseConnection())
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}

function sectionCollapsedStorageKey(key: string) {
  return `Navigation section collapsed - ${key}`
}
